import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    console.error("[ErrorBoundary] Caught error:", error);
    return { 
      hasError: true,
      error 
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[ErrorBoundary] Component stack:", errorInfo.componentStack);
    this.setState({
      error,
      errorInfo
    });
  }

  private handleRetry = () => {
    console.log("[ErrorBoundary] Attempting recovery...");
    this.setState({ 
      hasError: false,
      error: null,
      errorInfo: null 
    });
  };

  private getErrorMessage(error: Error | null): string {
    if (!error) return "An unknown error occurred";
    
    // Handle specific error types
    if (error.message.includes("subscription")) {
      return "There was an error checking your subscription status. Please try again.";
    }
    if (error.message.includes("network")) {
      return "A network error occurred. Please check your connection and try again.";
    }
    if (error.message.includes("auth")) {
      return "There was an authentication error. Please try logging in again.";
    }
    
    // Default error message
    return error.message || "Something went wrong. Please try again.";
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <AlertDialog open={true}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Something went wrong</AlertDialogTitle>
              <AlertDialogDescription>
                {this.getErrorMessage(this.state.error)}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction asChild>
                <Button 
                  onClick={this.handleRetry}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Try Again
                </Button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      );
    }

    return this.props.children;
  }
}